<script lang="ts">
    import ActionConfirmation from '../../lib/Actions/ActionConfirmation.svelte';
    import ConfirmationPageSkeleton from '../../lib/Petitions/ConfirmationPageSkeleton.svelte';
    import { confirmationData, clearConfirmationData, type ConfirmationData } from '../../stores/confirmationStore';
    import { getPetitionById, getPetitions, getPetitionShareUrls, getUserPetitionSignatureStatus, getSignedPetitionIdsForUser } from '../../api/petitions';
    import { associationIdName, getQueryParam, vvSettings } from '../../api/common';
    import * as t from '../../translations/Petitions.ts';

    // Get base URL for sharing - use dev URL on localhost for testing
    function getShareBaseUrl(): string {
        const origin = window.location.origin;
        if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
            return 'https://dev.votervoice.net';
        }
        return origin;
    }

    function getGenericShareUrl(petitionUrl: string): string {
        return `${getShareBaseUrl()}${petitionUrl}`;
    }

    function getShareTokenStorageKey(petitionId: number): string {
        return `vv-petition-share-user-token-${petitionId}`;
    }

    // Route parameter
    export let params: { id?: string } = {};

    /** Same as sign page: only one active petition, so "Back to Petitions" would loop. */
    $: hideBackToPetitions = getQueryParam('fromSingle') === '1';

    let loading = true;
    let data: ConfirmationData | null = null;
    let error: string | null = null;
    let lastLoadedPetitionId: number | null = null;
    let activeLoadRequest = 0;

    function getSiteSegment(): string {
        const rawSiteNumber = (vvSettings as any)?.siteNumber;
        return rawSiteNumber ? `/${rawSiteNumber}` : '';
    }

    async function loadConfirmationData(petitionId: number) {
        const requestId = ++activeLoadRequest;
        loading = true;
        data = null;
        error = null;

        // First, try to get data from the store (passed from SignPage)
        const storeData = $confirmationData;
        if (storeData && storeData.petitionId === petitionId) {
            if (requestId !== activeLoadRequest) return;
            data = storeData;
            loading = false;
            return;
        }

        // Direct navigation to confirmation page - handles refresh/deep-link
        try {
            const petition = await getPetitionById(petitionId);
            if (!petition) {
                if (requestId !== activeLoadRequest) return;
                error = 'Petition not found';
                loading = false;
                return;
            }

            const response = await getPetitions();
            const allPetitions = [...response.spotlightPetitions, ...response.otherPetitions];
            let relatedPetitions = allPetitions
                .filter(p => p.id !== petitionId)
                .slice(0, 3)
                .map(p => ({
                    id: p.id,
                    title: p.title,
                    imageUrl: p.imageUrl || null,
                    signatureCount: p.signatureCount || 0
                }));

            const storedUserToken = sessionStorage.getItem(getShareTokenStorageKey(petition.id))
                || (window as any).vvSettings?.userContainer?.userModel?.userToken
                || undefined;
            if (storedUserToken) {
                const signedRelated = await getSignedPetitionIdsForUser(
                    storedUserToken,
                    relatedPetitions.map((r) => r.id)
                );
                relatedPetitions = relatedPetitions.map((r) => ({
                    ...r,
                    hasSigned: signedRelated.has(r.id)
                }));
            }

            const shareUrls = await getPetitionShareUrls(petition.id, storedUserToken, getQueryParam('vvsn') || undefined);
            const signatureStatus = storedUserToken
                ? await getUserPetitionSignatureStatus(petition.id, storedUserToken)
                : null;

            if (requestId !== activeLoadRequest) return;
            data = {
                petitionId: petition.id,
                petitionTitle: petition.title,
                signatureCount: petition.signatureCount && petition.signatureCount > 0 ? petition.signatureCount : undefined,
                signatureGoal: petition.signatureGoal ?? null,
                shareUrl: shareUrls?.webShareUrl || getGenericShareUrl(petition.url),
                facebookShareUrl: shareUrls?.facebookShareUrl,
                twitterShareUrl: shareUrls?.twitterShareUrl,
                linkedInShareUrl: shareUrls?.linkedInShareUrl,
                thankYouMessage: petition.confirmationMessage || 'Thank you for signing this petition!',
                sharingEnabled: petition.sharingEnabled ?? true,
                relatedPetitions,
                datePreviouslySigned: signatureStatus?.dateSigned || null
            };
            loading = false;
        } catch (err) {
            console.error('Failed to load confirmation data:', err);
            if (requestId !== activeLoadRequest) return;
            error = 'Failed to load confirmation data';
            loading = false;
        }

        // Defensive fallback: avoid blank page when no branch renders.
        if (!loading && !error && !data) {
            error = 'Unable to load confirmation details. Please return to petitions and try again.';
        }
    }

    $: {
        const petitionId = params.id ? parseInt(params.id, 10) : null;
        if (petitionId && !Number.isNaN(petitionId)) {
            if (petitionId !== lastLoadedPetitionId) {
                lastLoadedPetitionId = petitionId;
                void loadConfirmationData(petitionId);
            }
        } else if (params.id) {
            loading = false;
            data = null;
            error = 'Invalid petition ID';
        }
    }

    function handleBackToPetitions() {
        clearConfirmationData();
        window.location.href = `/${associationIdName}${getSiteSegment()}/Petitions`;
    }

    function handleGoHome() {
        clearConfirmationData();
        window.location.href = `/${associationIdName}/home`;
    }

    function handleViewOther() {
        clearConfirmationData();
        // Navigate to old MVC home page (full page navigation)
        window.location.href = `/${associationIdName}/home`;
    }

    function handleSignRelated(petition: { id: number }) {
        clearConfirmationData();
        window.location.href = `/${associationIdName}${getSiteSegment()}/Petitions/${petition.id}/Respond`;
    }
</script>

{#if loading}
    <ConfirmationPageSkeleton />
{:else if error}
    <div class="max-w-3xl mx-auto px-4 md:px-6 py-8" data-testid="confirmation-error">
        <div class="text-center">
            <h1 class="!text-2xl !leading-8 !font-bold !text-gray-900 mb-4">Something went wrong</h1>
            <p class="!text-base !leading-6 !text-gray-600 mb-6" data-testid="confirmation-error-message">{error}</p>
            {#if !hideBackToPetitions}
                <button
                    class="px-6 py-3 !min-h-[48px] bg-teal-600 !text-white !text-base !leading-6 !font-medium rounded-lg hover:bg-teal-700 transition-colors"
                    on:click={handleBackToPetitions}
                    data-testid="confirmation-back-button"
                >
                    Back to Petitions
                </button>
            {:else}
                <button
                    class="px-6 py-3 !min-h-[48px] bg-teal-600 !text-white !text-base !leading-6 !font-medium rounded-lg hover:bg-teal-700 transition-colors"
                    on:click={handleGoHome}
                    data-testid="confirmation-home-button"
                >
                    {t.Home_Label()}
                </button>
            {/if}
        </div>
    </div>
{:else if data}
    <div class="max-w-3xl mx-auto px-4 md:px-6 py-8" data-testid="confirmation-page">
        <ActionConfirmation
            title={t.Thank_You_Title()}
            message={t.Thank_You_Message()}
            actionType="petition"
            petitionId={data.petitionId}
            signatureCount={data.signatureCount}
            signatureGoal={data.signatureGoal}
            shareUrl={data.shareUrl}
            facebookShareUrl={data.facebookShareUrl}
            twitterShareUrl={data.twitterShareUrl}
            linkedInShareUrl={data.linkedInShareUrl}
            sharingEnabled={data.sharingEnabled}
            thankYouMessage={data.thankYouMessage}
            petitionTitle={data.petitionTitle}
            relatedPetitions={data.relatedPetitions}
            datePreviouslySigned={data.datePreviouslySigned || null}
            hideBackToPetitions={hideBackToPetitions}
            onBack={handleBackToPetitions}
            onViewOther={handleViewOther}
            onSignRelated={handleSignRelated}
        />
    </div>
{/if}

